// Product Detail Page — /p/:handle
//
// Contract (PROMPT 2.4):
//   - Two-column layout on desktop: image gallery (7/12) + sticky buy box (5/12).
//   - Single column on mobile: gallery → buy box inline, no fixed bottom bar.
//   - Variant selector: one button row per option axis (option1 × option2 × ...).
//   - Pre-order iff (quantityAvailable == 0 && availableForSale == true) OR
//     tags.includes('preorder'). Button flips to 'Reserve Now — Full Pre-Paid'.
//   - Add to Cart POSTs /api/cart/lines via CartProvider.addLine.
//   - Selection changes URL to /p/:handle?variant=<id>.

import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate, useParams, useSearchParams } from 'react-router-dom';
import { useQuery } from '@tanstack/react-query';
import TopUtilityBar from '../sections/TopUtilityBar';
import MainHeader from '../sections/MainHeader';
import Footer from '../sections/Footer';
import { productByHandle } from '../lib/shopify/queries';
import { useCart } from '../state/cart';
import { useToast } from '../components/loadout/Toast';
import PreorderEtaBadge from '../components/PreorderEtaBadge';
import AffordabilityWidget from '../components/pdp/AffordabilityWidget';
import CompleteLoadout, { completeLoadoutKind } from '../components/pdp/CompleteLoadout';
import KeyFeatures from '../components/pdp/KeyFeatures';
import NeedBackup from '../components/pdp/NeedBackup';
import UpgradePopup from '../components/pdp/UpgradePopup';
import StickyAddToCart from '../components/pdp/StickyAddToCart';
import RestockNotify from '../components/pdp/RestockNotify';
import { useProductReviews, ReviewSummary, ReviewsList } from '../components/pdp/Reviews';
import { getEta } from '../data/preorderEta';
import { PDP } from '../constants/testIds';
import { clipText, useCanonical, useJsonLd, useSeoMeta } from '../lib/seo';
import draftProductRedirects from '../data/draft-product-redirects.json';
import Breadcrumbs from '../components/Breadcrumbs';
import { collectionForType } from '../lib/catalog/categorySeo';
import { isWarrantyEligible, isReturnsOnly, fetchWarrantyVariantId, warrantyOf, warrantyLineFor } from '../lib/warrantyAddon';

function stripHtml(s) {
        return String(s || '').replace(/<[^>]*>/g, ' ').replace(/\s+/g, ' ').trim();
}

function buildProductJsonLd(product, variant, reviews) {
        const price = variant?.price || product.priceRange?.minVariantPrice;
        const inStock = variant ? variant.availableForSale !== false : product.availableForSale !== false;
        const description = stripHtml(product.description || product.descriptionHtml).slice(0, 500);
        const ld = {
                '@context': 'https://schema.org/',
                '@type': 'Product',
                name: product.title,
                image: product.featuredImage?.url || product.images?.[0]?.url || undefined,
                description,
                sku: variant?.sku || product.firstVariant?.sku || undefined,
                brand: { '@type': 'Brand', name: 'Loadout' },
                offers: {
                        '@type': 'Offer',
                        url: `https://loadout.co.in/p/${product.handle}`,
                        priceCurrency: price?.currencyCode || 'INR',
                        price: price?.amount != null ? String(price.amount) : undefined,
                        availability: `https://schema.org/${inStock ? 'InStock' : 'OutOfStock'}`,
                        itemCondition: 'https://schema.org/NewCondition',
                },
        };
        // Only emit rating markup when approved reviews actually exist.
        if (reviews?.count > 0) {
                ld.aggregateRating = {
                        '@type': 'AggregateRating',
                        ratingValue: String(reviews.average),
                        reviewCount: String(reviews.count),
                        bestRating: '5',
                        worstRating: '1',
                };
                ld.review = reviews.reviews.slice(0, 10).map((r) => ({
                        '@type': 'Review',
                        author: { '@type': 'Person', name: r.author_name },
                        datePublished: r.created_at ? r.created_at.slice(0, 10) : undefined,
                        name: r.title || undefined,
                        reviewBody: r.body,
                        reviewRating: { '@type': 'Rating', ratingValue: String(r.rating), bestRating: '5' },
                }));
        }
        return ld;
}

// ---------- helpers ----------
const CDN_HOSTS = ['cdn.shopify.com'];

function isShopifyCdn(url) {
        if (!url) return false;
        try {
                const u = new URL(url);
                return CDN_HOSTS.some((h) => u.hostname.endsWith(h));
        } catch {
                return false;
        }
}

function formatINR(money) {
        if (!money || money.amount == null) return null;
        const n = Number(money.amount);
        if (!Number.isFinite(n)) return null;
        // "en-IN" gives the ₹1,23,456 grouping. Fall back to plain if unavailable.
        try {
                return n.toLocaleString('en-IN', { style: 'currency', currency: money.currencyCode || 'INR', maximumFractionDigits: 0 });
        } catch {
                return `₹${n.toLocaleString()}`;
        }
}

function isPreorderVariant(variant, productTags = []) {
        if (!variant) return false;
        if (Array.isArray(productTags) && productTags.map(String).map((t) => t.toLowerCase()).includes('preorder')) return true;
        const q = variant.quantityAvailable;
        return typeof q === 'number' && q <= 0 && variant.availableForSale === true;
}

function isSoldOut(variant) {
        if (!variant) return true;
        return variant.availableForSale === false;
}

// Build option axes → button rows.
function buildOptionAxes(product) {
        const options = product.options || [];
        return options.map((opt) => ({ name: opt.name, values: opt.values || [] }));
}

// Find variant that matches the current selection map { [axisName]: value }.
function findVariant(product, selection) {
        const edges = product?.variants?.edges || [];
        return edges
                .map((e) => e.node)
                .find((v) =>
                        (v.selectedOptions || []).every((so) => selection[so.name] === so.value)
                );
}

// Build the set of (axisName, value) tuples that EXIST on this product
// (regardless of inventory). Used by the strike-through logic in
// VariantAxis — a combo is "missing" iff Shopify has no variant row for it,
// not just because that row is sold-out. opts.includeUnavailable=false
// keeps the historical behaviour used by snapSelection, which should
// prefer in-stock combos when snapping.
function buildRealCombos(product, opts = {}) {
        const { includeUnavailable = true } = opts;
        const edges = product?.variants?.edges || [];
        const combos = edges
                .filter((e) => includeUnavailable ? true : e?.node?.availableForSale !== false)
                .map((e) => {
                        const m = {};
                        for (const so of e.node.selectedOptions || []) m[so.name] = so.value;
                        return m;
                });
        return combos;
}

// When the user clicks a value on `changedAxis`, keep as much of their prior
// selection as possible; if the resulting combo has no matching real variant,
// pick the first real combo that contains {changedAxis: newValue} and snap
// the other axes to match it.
function snapSelection(product, prior, changedAxis, newValue) {
        const combos = buildRealCombos(product, { includeUnavailable: false });
        // Best case: keep everything else and just flip the changed axis.
        const proposed = { ...prior, [changedAxis]: newValue };
        const exact = combos.find((c) =>
                Object.keys(proposed).every((k) => c[k] === proposed[k])
        );
        if (exact) return proposed;
        // Fallback: find any real variant with the newly-chosen value on
        // changedAxis, prefer one that preserves the most other-axis values.
        const withValue = combos.filter((c) => c[changedAxis] === newValue);
        if (withValue.length === 0) return proposed;
        withValue.sort((a, b) => {
                const scoreA = Object.keys(prior).filter((k) => k !== changedAxis && prior[k] === a[k]).length;
                const scoreB = Object.keys(prior).filter((k) => k !== changedAxis && prior[k] === b[k]).length;
                return scoreB - scoreA;
        });
        return withValue[0];
}

// Initial selection: preselected variant from ?variant=<id>, or the first
// variant with quantityAvailable>0, or the first variant overall.
function initialSelection(product, urlVariantId) {
        const edges = product?.variants?.edges || [];
        const variants = edges.map((e) => e.node);
        let chosen =
                (urlVariantId && variants.find((v) => v.id === urlVariantId || v.id.endsWith(`/${urlVariantId}`))) ||
                variants.find((v) => (v.quantityAvailable || 0) > 0) ||
                variants[0];
        if (!chosen) return {};
        const sel = {};
        for (const so of chosen.selectedOptions || []) sel[so.name] = so.value;
        return sel;
}

// ---------- component ----------
export default function ProductDetailPage() {
        const { handle } = useParams();
        const [searchParams, setSearchParams] = useSearchParams();

        const { data: product, isLoading, isError } = useQuery({
                queryKey: ['shopify', 'product', handle],
                queryFn: () => productByHandle(handle),
                staleTime: 5 * 60_000,
                retry: 1,
        });

        useCanonical(`/p/${handle}`);
        // Unknown handle: follow a configured redirect (dead/draft product), else noindex.
        const navigate = useNavigate();
        const notFound = !isLoading && (isError || !product);
        const redirectTo = notFound ? draftProductRedirects[handle] : null;
        useEffect(() => {
                if (redirectTo) navigate(redirectTo, { replace: true });
        }, [redirectTo, navigate]);
        useSeoMeta(notFound ? { title: "Product not found — Loadout", noindex: true } : null);

        return (
                <div className="min-h-screen bg-[#0A0A0A] text-white">
                        <TopUtilityBar />
                        <MainHeader />
                        <main data-testid={PDP.page} className="mx-auto max-w-[1440px] px-6 py-10 lg:py-16">
                                {isLoading && (
                                        <p className="font-mono text-[12px] tracking-[0.2em] text-white/50 uppercase">
                                                Loading…
                                        </p>
                                )}
                                {isError && (
                                        <p className="font-mono text-[12px] tracking-[0.2em] text-white/50 uppercase">
                                                Product not found.
                                        </p>
                                )}
                                {product && (
                                        <PdpBody
                                                product={product}
                                                initialUrlVariantId={searchParams.get('variant')}
                                                onSelectVariant={(id) => {
                                                        const params = new URLSearchParams(searchParams);
                                                        params.set('variant', id);
                                                        setSearchParams(params, { replace: true });
                                                }}
                                        />
                                )}
                        </main>
                        <Footer />
                </div>
        );
}

function PdpBody({ product, initialUrlVariantId, onSelectVariant }) {
        const axes = useMemo(() => buildOptionAxes(product), [product]);
        const [selection, setSelection] = useState(() => initialSelection(product, initialUrlVariantId));
        // Strike-out only kicks in AFTER the shopper has picked a variant option.
        // On first load everything is shown as a normal pickable chip; otherwise
        // the alternate values appear struck-through on page load which reads
        // like the options aren't really there.
        const [hasPickedVariant, setHasPickedVariant] = useState(false);

        const activeVariant = useMemo(() => findVariant(product, selection), [product, selection]);
        const reviews = useProductReviews(product.handle);
        const loadoutKind = completeLoadoutKind(product.productType);

        // Product JSON-LD reflects the currently selected variant (price,
        // sku, availability) so rich-result markup matches what buyers see.
        useJsonLd('pdp-product', useMemo(
                () => buildProductJsonLd(product, activeVariant, reviews),
                [product, activeVariant, reviews],
        ));
        const minAmount = Number(product.priceRange?.minVariantPrice?.amount || 0);
        const brand = product.vendor && !product.title.toLowerCase().startsWith(product.vendor.toLowerCase())
                ? `${product.vendor} ` : '';
        useSeoMeta({
                title: `${brand}${product.title}${minAmount ? ` — ₹${minAmount.toLocaleString('en-IN')}` : ''} | Buy in India | Loadout`,
                description: clipText(
                        `${stripHtml(product.description || product.descriptionHtml)} ` +
                        'Buy at Loadout India — UPI, 18-month warranty, WhatsApp support.',
                ),
                image: product.images?.[0]?.url,
                path: `/p/${product.handle}`,
                type: 'product',
        });

        // Sync URL when variant changes
        useEffect(() => {
                if (activeVariant?.id) onSelectVariant(activeVariant.id);
                // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [activeVariant?.id]);

        // Image gallery — keyed to the SELECTED VARIANT, not just the
        // product.images list. variant.image is authoritative (Storefront
        // returns up to one image per variant). If the variant's image URL
        // exists in the gallery, jump the gallery to it; if the variant
        // image is beyond the gallery payload (products with >N images),
        // show it directly as an override so the swap is never dropped.
        const images = product.images || [];
        const [activeImageIdx, setActiveImageIdx] = useState(0);
        const [variantOverride, setVariantOverride] = useState(null); // {url, altText} | null
        const activeVariantId = activeVariant?.id;
        useEffect(() => {
                const vImg = activeVariant?.image;
                if (!vImg?.url) return;
                const idx = images.findIndex((i) => i.url === vImg.url);
                if (idx >= 0) {
                        setActiveImageIdx(idx);
                        setVariantOverride(null);
                } else {
                        setVariantOverride({ url: vImg.url, altText: vImg.altText || product.title });
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [activeVariantId]);
        const primaryImage = variantOverride || images[activeImageIdx] || null;

        // Touch swipe on the primary image (mobile) — steps through the gallery.
        const touchRef = useRef(null);
        const buyBtnRef = useRef(null);
        const onTouchStart = (e) => {
                const t = e.touches[0];
                touchRef.current = { x: t.clientX, y: t.clientY };
        };
        const onTouchEnd = (e) => {
                const start = touchRef.current;
                touchRef.current = null;
                if (!start || images.length < 2) return;
                const t = e.changedTouches[0];
                const dx = t.clientX - start.x;
                if (Math.abs(dx) < 40 || Math.abs(dx) < Math.abs(t.clientY - start.y)) return;
                const base = variantOverride ? -1 : activeImageIdx;
                const next = dx < 0
                        ? (base + 1) % images.length
                        : (base - 1 + images.length) % images.length;
                setVariantOverride(null);
                setActiveImageIdx(next);
        };

        // Cart
        const { addLine, addWarranty, totalQuantity, restored, consumeRestored } = useCart();
        const [toast, showToast] = useToast();

        // Extended-warranty upsell (pre-cart checkbox). Resolves only for
        // eligible product types once the warranty product is published.
        const warrantyEligible = isWarrantyEligible(product?.productType);
        const [warrantyVariantId, setWarrantyVariantId] = useState(null);
        const [warrantyChecked, setWarrantyChecked] = useState(false);
        useEffect(() => {
                let alive = true;
                if (warrantyEligible) {
                        fetchWarrantyVariantId().then((v) => {
                                if (alive && v) setWarrantyVariantId(v);
                        });
                }
                return () => { alive = false; };
        }, [warrantyEligible]);

        // One-shot "Restored your cart" toast on mount
        useEffect(() => {
                if (restored) {
                        showToast('Restored your cart');
                        consumeRestored();
                }
                // eslint-disable-next-line react-hooks/exhaustive-deps
        }, [restored]);

        const preorder = isPreorderVariant(activeVariant, product.tags);
        const soldOut = isSoldOut(activeVariant);
        // Final payable amount in paise for the Razorpay affordability
        // widget — tracks the selected variant, falls back to min price.
        const affordabilityPaise = useMemo(() => {
                const raw = activeVariant?.price?.amount ?? product?.priceRange?.minVariantPrice?.amount;
                const rupees = Number(raw);
                return Number.isFinite(rupees) && rupees > 0 ? Math.round(rupees * 100) : 0;
        }, [activeVariant, product]);

        const price = activeVariant?.price || product.priceRange?.minVariantPrice;
        const compareAt = activeVariant?.compareAtPrice || product.compareAtPriceRange?.minVariantPrice;
        const priceStr = formatINR(price);
        const compareStr = formatINR(compareAt);
        const showCompare =
                priceStr && compareStr && Number(compareAt?.amount || 0) > Number(price?.amount || 0);
        const savingsAmount = showCompare
                ? Number(compareAt.amount) - Number(price.amount)
                : 0;
        const savingsStr = savingsAmount > 0 ? formatINR({ amount: savingsAmount, currencyCode: price?.currencyCode }) : null;

        let ctaLabel = 'Add to Cart';
        let ctaKind = 'buy';
        if (soldOut) {
                ctaLabel = 'Sold Out';
        } else if (preorder) {
                ctaLabel = 'Reserve Now — Full Pre-Paid';
                ctaKind = 'preorder';
        }

        const [busy, setBusy] = useState(false);
        const handleAdd = async () => {
                if (!activeVariant || soldOut || busy) return;
                setBusy(true);
                const res = await addLine(activeVariant.id, { intent: preorder ? 'preorder' : 'buy', addedFrom: 'pdpage.v1' });
                if (res.ok && warrantyChecked && warrantyVariantId) {
                        // Attach the hidden warranty line to the parent line.
                        // Dedupe: skip if a warranty line already references it.
                        const parent = (res.lines || []).find(
                                (l) => (l?.merchandise?.id) === activeVariant.id && !warrantyOf(l)
                        );
                        if (parent && !warrantyLineFor(parent.id, res.lines)) {
                                await addWarranty(warrantyVariantId, parent.id);
                        }
                }
                setBusy(false);
                if (res.ok) {
                        showToast(preorder ? 'Reserved' : 'Added', { kind: 'success' });
                } else {
                        showToast('Try again', {});
                }
        };

        const inventoryChip = soldOut
                ? { label: 'Sold Out', tone: 'red' }
                : preorder
                        ? { label: 'Available via pre-order', tone: 'orange' }
                        : {
                                        label:
                                                typeof activeVariant?.quantityAvailable === 'number' && activeVariant.quantityAvailable <= 3
                                                        ? `${activeVariant.quantityAvailable} left`
                                                        : 'In stock',
                                        tone: 'green',
                                };

        const inStockCopy = 'In stock — ships in 3–5 days.';
        const soldOutCopy = 'Currently sold out — get an email the moment it’s back below.';
        const preorderCopy =
                'Charged in full today. Ships once back in stock — you’ll get a tracking link by email the day of dispatch.';

        return (
                <>
                        {toast}
                        <Breadcrumbs
                                className="mb-6"
                                items={[
                                        { name: 'Home', path: '/' },
                                        ...(collectionForType(product.productType)
                                                ? [{ name: collectionForType(product.productType).name, path: `/collections/${collectionForType(product.productType).handle}` }]
                                                : []),
                                        { name: product.title, path: `/p/${product.handle}` },
                                ]}
                        />
                        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 lg:gap-12">
                                {/* Gallery */}
                                <section
                                        data-testid={PDP.gallery}
                                        className="lg:col-span-7 flex flex-col lg:flex-row-reverse gap-4"
                                >
                                        {/* Primary image */}
                                        <div
                                                data-testid="pdp-gallery-swipe-area"
                                                onTouchStart={onTouchStart}
                                                onTouchEnd={onTouchEnd}
                                                className="relative flex-1 border border-white/10 bg-[#111] min-h-[360px] lg:min-h-[560px] flex items-center justify-center overflow-hidden touch-pan-y"
                                        >
                                                {primaryImage ? (
                                                        <img
                                                                key={primaryImage.url}
                                                                data-testid={PDP.primaryImage}
                                                                src={primaryImage.url}
                                                                alt={primaryImage.altText || product.title}
                                                                loading="eager"
                                                                draggable={false}
                                                                className="w-full h-full object-contain p-6 select-none animate-[fadeIn_200ms_ease-out]"
                                                                style={{ maxHeight: '640px' }}
                                                        />
                                                ) : (
                                                        <span className="font-display text-white/20 text-[80px]">{product.title?.[0] || '?'}</span>
                                                )}
                                                {images.length > 1 && (
                                                        <div data-testid="pdp-gallery-dots" className="lg:hidden absolute bottom-3 inset-x-0 flex justify-center gap-1.5 pointer-events-none">
                                                                {images.map((img, i) => (
                                                                        <span
                                                                                key={img.url + i}
                                                                                className={'h-1.5 transition-all duration-200 ' + (i === activeImageIdx && !variantOverride ? 'w-5 bg-[#FF9F1C]' : 'w-1.5 bg-white/30')}
                                                                        />
                                                                ))}
                                                        </div>
                                                )}
                                                {!isShopifyCdn(primaryImage?.url) && primaryImage?.url && (
                                                        <span className="absolute top-3 left-3 font-mono text-[9px] uppercase tracking-[0.2em] text-red-400/70">
                                                                non-cdn
                                                        </span>
                                                )}
                                        </div>

                                        {/* Thumbnails */}
                                        {images.length > 1 && (
                                                <div
                                                        data-testid={PDP.thumbs}
                                                        className="flex lg:flex-col gap-2 lg:w-[84px] overflow-x-auto lg:overflow-y-auto lg:max-h-[560px]"
                                                >
                                                        {images.map((img, i) => (
                                                                <button
                                                                        key={img.url + i}
                                                                        type="button"
                                                                        onClick={() => { setActiveImageIdx(i); setVariantOverride(null); }}
                                                                        data-testid={PDP.thumb}
                                                                        aria-label={`View image ${i + 1}`}
                                                                        className={
                                                                                'shrink-0 w-[76px] h-[76px] border transition-colors flex items-center justify-center bg-[#111] ' +
                                                                                (i === activeImageIdx && !variantOverride
                                                                                        ? 'border-[#FF9F1C]'
                                                                                        : 'border-white/10 hover:border-white/40')
                                                                        }
                                                                >
                                                                        <img
                                                                                src={img.url}
                                                                                alt={img.altText || product.title}
                                                                                loading="lazy"
                                                                                className="max-w-full max-h-full object-contain p-1.5"
                                                                        />
                                                                </button>
                                                        ))}
                                                </div>
                                        )}
                                </section>

                                {/* Buy Box */}
                                <aside
                                        data-testid={PDP.buyBox}
                                        className="lg:col-span-5"
                                >
                                        <p className="font-mono text-[11px] tracking-[0.18em] text-[#FF9F1C]">
                                                {(product.vendor || 'LOADOUT').toUpperCase()}
                                                {product.productType ? ` / ${product.productType.toUpperCase()}` : ''}
                                        </p>
                                        <h1
                                                data-testid={PDP.title}
                                                className="mt-3 font-display text-3xl sm:text-4xl leading-tight uppercase"
                                        >
                                                {product.title}
                                        </h1>

                                        <ReviewSummary handle={product.handle} summary={reviews} />

                                        {/* Price */}
                                        <div className="mt-5 flex items-baseline gap-3 flex-wrap">
                                                <span data-testid={PDP.priceCurrent} className="font-mono text-[26px] text-white tabular-nums">
                                                        {priceStr || '—'}
                                                </span>
                                                {showCompare && (
                                                        <span
                                                                data-testid={PDP.priceCompare}
                                                                className="font-mono text-[16px] text-white/40 line-through tabular-nums"
                                                        >
                                                                {compareStr}
                                                        </span>
                                                )}
                                                {savingsStr && (
                                                        <span className="font-mono text-[11px] tracking-[0.16em] uppercase text-[#FF9F1C]">
                                                                Save {savingsStr}
                                                        </span>
                                                )}
                                        </div>

                                        {/* Inventory chip */}
                                        <div className="mt-3">
                                                <span
                                                        data-testid={PDP.inventoryChip}
                                                        className={
                                                                'inline-block border font-mono text-[10px] tracking-[0.2em] uppercase px-2.5 py-1.5 ' +
                                                                (inventoryChip.tone === 'orange'
                                                                        ? 'border-[#FF9F1C] text-[#FF9F1C]'
                                                                        : inventoryChip.tone === 'red'
                                                                                ? 'border-red-500 text-red-400'
                                                                                : 'border-emerald-500/60 text-emerald-400')
                                                        }
                                                >
                                                        {inventoryChip.label}
                                                </span>
                                        </div>

                                        {/* Live per-variant stock line — only while the selected
                                             variant has qty > 0; disappears on its own at 0 or below. */}
                                        {typeof activeVariant?.quantityAvailable === 'number' && activeVariant.quantityAvailable > 0 && (
                                                <p
                                                        data-testid="pdp-variant-in-stock-line"
                                                        className="mt-3 flex items-center gap-2.5 border border-emerald-500/40 bg-emerald-500/10 px-3 py-2 font-mono text-[11px] tracking-[0.12em] uppercase text-emerald-300"
                                                >
                                                        <span className="relative flex h-2 w-2 shrink-0">
                                                                <span className="absolute inline-flex h-full w-full rounded-full bg-emerald-400 opacity-75 animate-ping" />
                                                                <span className="relative inline-flex h-2 w-2 rounded-full bg-emerald-400" />
                                                        </span>
                                                        {activeVariant.title && activeVariant.title !== 'Default Title'
                                                                ? `${activeVariant.title} is in stock — ready to dispatch`
                                                                : 'In stock — ready to dispatch'}
                                                </p>
                                        )}

                                        {/* Extended-warranty disclosure — eligible types only
                                             (mice / keyboards / audio / headphone / microphone). */}
                                        {isWarrantyEligible(product.productType) && (
                                                <p
                                                        data-testid="pdp-warranty-disclosure"
                                                        className="mt-3 font-mono text-[10px] tracking-[0.12em] uppercase leading-relaxed text-white/50"
                                                >
                                                        Comes with an 18-month warranty.
                                                        Add 6 months of extended warranty (+₹1,000) in cart
                                                        for a total 24-month term.
                                                </p>
                                        )}

                                        {/* Preorder ETA — sits between inventory chip and variant selector.
                                             Returns null (renders nothing) for in-stock SKUs. */}
                                        <div className="mt-2">
                                                <PreorderEtaBadge
                                                        etaWindow={getEta(product.handle, { isPreorder: preorder })}
                                                />
                                        </div>

                                        {/* Razorpay Affordability widget (EMI / Pay Later) — sits directly
                                             below the delivery timeline. Amount tracks the selected
                                             variant's price; widget hides options under Razorpay's
                                             minimum-order limits automatically. */}
                                        <AffordabilityWidget amountInPaise={affordabilityPaise} />

                                        {/* Variant axes */}
                                        <div className="mt-8 space-y-6">
                                                {axes.map((axis) => (
                                                        <VariantAxis
                                                                key={axis.name}
                                                                axis={axis}
                                                                selection={selection}
                                                                setSelection={(upd) => {
                                                                        setHasPickedVariant(true);
                                                                        setSelection(upd);
                                                                }}
                                                                product={product}
                                                                showIncompatible={hasPickedVariant}
                                                        />
                                                ))}
                                        </div>

                                        {/* Extended-warranty upsell — pre-cart checkbox.
                                             Visible only for eligible types once the hidden
                                             warranty product resolves (Active + headless channel). */}
                                        {warrantyEligible && warrantyVariantId && (
                                                <label
                                                        data-testid="pdp-warranty-upsell"
                                                        className="mt-6 flex items-start gap-3 border border-dashed border-[#FF9F1C]/50 hover:border-[#FF9F1C] px-4 py-3 cursor-pointer transition-colors"
                                                >
                                                        <input
                                                                type="checkbox"
                                                                checked={warrantyChecked}
                                                                onChange={(e) => setWarrantyChecked(e.target.checked)}
                                                                data-testid="pdp-warranty-upsell-checkbox"
                                                                className="mt-0.5 h-4 w-4 accent-[#FF9F1C]"
                                                        />
                                                        <span className="font-mono text-[11px] tracking-[0.1em] uppercase leading-relaxed text-white/80">
                                                                Add 6 months of extended warranty
                                                                <span className="text-[#FF9F1C]"> (+₹1,000)</span>
                                                                <span className="block mt-1 text-white/45 normal-case tracking-normal font-sans text-[12px]">
                                                                        Total 24-month coverage. Added to your cart as a separate line.
                                                                </span>
                                                        </span>
                                                </label>
                                        )}

                                        {/* Add to Cart */}
                                        <button
                                                ref={buyBtnRef}
                                                type="button"
                                                data-testid={PDP.buyButton}
                                                onClick={handleAdd}
                                                disabled={soldOut || busy || !activeVariant}
                                                className={
                                                        'mt-8 w-full font-mono text-[12px] tracking-[0.2em] uppercase py-4 border transition-colors ' +
                                                        (soldOut
                                                                ? 'border-white/15 text-white/40 cursor-not-allowed'
                                                                : preorder
                                                                        ? 'border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] hover:bg-transparent hover:text-[#FF9F1C]'
                                                                        : 'border-[#FF9F1C] bg-[#FF9F1C] text-[#0A0A0A] hover:bg-transparent hover:text-[#FF9F1C]')
                                                }
                                        >
                                                {busy ? 'Adding…' : ctaLabel}
                                        </button>

                                        {/* Companion copy */}
                                        <p
                                                data-testid={PDP.companionCopy}
                                                className="mt-3 font-sans text-[12px] text-white/60 leading-relaxed"
                                        >
                                                {soldOut ? soldOutCopy : preorder ? preorderCopy : inStockCopy}
                                        </p>
                                        {(soldOut || (activeVariant?.availableForSale && typeof activeVariant?.quantityAvailable === 'number' && activeVariant.quantityAvailable <= 0)) && (
                                                <RestockNotify handle={product.handle} variant={activeVariant} kind={soldOut ? 'soldout' : 'preorder'} />
                                        )}

                                        {/* 18-month warranty section — eligible types only */}
                                        {warrantyEligible && (
                                                <section
                                                        data-testid="pdp-warranty-section"
                                                        className="mt-6 border border-white/10 bg-white/[0.02] px-4 py-4"
                                                >
                                                        <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#FF9F1C]">
                                                                18-Month Warranty, Standard
                                                        </p>
                                                        <p className="mt-2 font-sans text-[12px] text-white/60 leading-relaxed">
                                                                Every unit ships covered for 18 months — doorstep pickup
                                                                on RMA (chargeable at ₹150); replacements ship back free.
                                                                Extend to 24 months for ₹1,000 with the checkbox above.
                                                        </p>
                                                        <a
                                                                href="/warranty"
                                                                data-testid="pdp-warranty-link"
                                                                className="mt-2 inline-block font-mono text-[10px] tracking-[0.16em] uppercase text-[#FF9F1C] hover:underline"
                                                        >
                                                                Warranty &amp; RMA details →
                                                        </a>
                                                </section>
                                        )}

                                        {/* Returns-only note — keycaps / switches / mousepads carry
                                             no warranty; the 7-day return policy applies instead. */}
                                        {isReturnsOnly(product.productType) && (
                                                <section
                                                        data-testid="pdp-returns-only-note"
                                                        className="mt-6 border border-white/10 bg-white/[0.02] px-4 py-4"
                                                >
                                                        <p className="font-mono text-[10px] tracking-[0.18em] uppercase text-[#FF9F1C]">
                                                                Returns Only — No Warranty
                                                        </p>
                                                        <p className="mt-2 font-sans text-[12px] text-white/60 leading-relaxed">
                                                                Keycaps, switches and mousepads are covered by the 7-day
                                                                return policy instead of a warranty. An unboxing video is
                                                                required for every return.
                                                        </p>
                                                        <a
                                                                href="/shipping-returns"
                                                                data-testid="pdp-returns-only-link"
                                                                className="mt-2 inline-block font-mono text-[10px] tracking-[0.16em] uppercase text-[#FF9F1C] hover:underline"
                                                        >
                                                                Return policy →
                                                        </a>
                                                </section>
                                        )}

                                        <KeyFeatures html={product.descriptionHtml} />
                                        <NeedBackup />
                                </aside>
                        </div>

                        <UpgradePopup handle={product.handle} />
                        <StickyAddToCart
                                targetRef={buyBtnRef}
                                priceStr={priceStr}
                                variantTitle={activeVariant?.title}
                                label={soldOut ? 'Sold Out' : preorder ? 'Reserve Now' : 'Add to Cart'}
                                onClick={handleAdd}
                                disabled={soldOut || busy || !activeVariant}
                                busy={busy}
                        />

                        {loadoutKind && <CompleteLoadout kind={loadoutKind} excludeHandle={product.handle} />}

                        {/* Description — full width, flows below the gallery + buy box */}
                        {(product.descriptionHtml || product.description) && (
                                <section
                                        data-testid="pdp-description-section"
                                        className="mt-10 pt-8 border-t border-white/10"
                                >
                                        {product.descriptionHtml ? (
                                                <div
                                                        className="pdp-description font-sans text-[14px] text-white/75 leading-relaxed max-w-[900px]"
                                                        dangerouslySetInnerHTML={{ __html: product.descriptionHtml }}
                                                />
                                        ) : (
                                                <p className="font-sans text-[14px] text-white/70 leading-relaxed whitespace-pre-line max-w-[900px]">
                                                        {product.description}
                                                </p>
                                        )}
                                </section>
                        )}

                        <ReviewsList handle={product.handle} summary={reviews} />
                </>
        );
}

function VariantAxis({ axis, selection, setSelection, product, showIncompatible = true }) {
        const activeValue = selection[axis.name];
        const realCombos = useMemo(() => buildRealCombos(product), [product]);
        return (
                <div data-testid={PDP.variantAxis}>
                        <p className="font-mono text-[10px] tracking-[0.2em] text-white/45 uppercase">
                                {axis.name}
                                {activeValue ? <span className="text-white/80"> — {activeValue}</span> : null}
                        </p>
                        <div className="mt-3 flex flex-wrap gap-2">
                                {axis.values.map((value) => {
                                        const isActive = activeValue === value;
                                        // Strike-out logic (only runs after first user interaction):
                                        //  - exists:        value appears on ANY variant on this product.
                                        //  - compatible:    value appears on a variant that ALSO matches
                                        //                   every OTHER axis currently selected.
                                        // Values that fail compatibility are struck-through (clear visual
                                        // cue the user would have to change another axis to pick this
                                        //  combo). They remain clickable — snapSelection snaps the other
                                        //  axes to the nearest valid combo on tap.
                                        const exists = realCombos.some((c) => c[axis.name] === value);
                                        const compatible = realCombos.some((c) =>
                                                c[axis.name] === value &&
                                                Object.keys(selection).every(
                                                        (k) => k === axis.name || selection[k] === c[k]
                                                )
                                        );
                                        const strike = showIncompatible && exists && !compatible && !isActive;
                                        const missing = showIncompatible && !exists && !isActive;
                                        return (
                                                <button
                                                        key={value}
                                                        type="button"
                                                        data-testid={PDP.variantOption}
                                                        data-variant-compatible={compatible || isActive ? "true" : "false"}
                                                        onClick={() => setSelection((prev) => snapSelection(product, prev, axis.name, value))}
                                                        className={
                                                                "font-mono text-[11px] tracking-[0.14em] uppercase border px-3.5 py-2 transition-colors " +
                                                                (isActive
                                                                        ? "border-[#FF9F1C] text-[#FF9F1C]"
                                                                        : missing
                                                                                ? "border-white/10 text-white/40 line-through grayscale hover:border-white/25"
                                                                                : strike
                                                                                        ? "border-white/10 text-white/40 line-through decoration-white/50 hover:border-white/30 hover:text-white/55"
                                                                                        : "border-white/20 text-white/85 hover:border-white/60")
                                                        }
                                                >
                                                        {value}
                                                </button>
                                        );
                                })}
                        </div>
                </div>
        );
}
